import { environment } from '../../../environments/environment';
import { APP_VERSION } from '../../version';
import { ConsoleBufferService } from './console-buffer.service';

interface JournalEntry {
  at: string;                      // ISO capture time
  kind: 'log' | 'health' | 'event';
  level: string | null;
  message: string;
}

/**
 * Continuous playback log. Mirrors every console line plus each playback
 * health check to the backend (playback_log table), so a stall leaves
 * evidence even when nobody files a bug report — until now the console
 * ring buffer only reached the server inside a report.
 *
 * Static and installed from main.ts, like ConsoleBufferService. Never logs
 * to the console itself (that would feed back into the queue).
 */
export class PlaybackJournal {
  private static readonly FLUSH_INTERVAL_MS = 60_000;
  private static readonly MAX_PENDING = 5000;
  private static readonly MAX_BATCH = 400;
  private static readonly MAX_BATCH_BYTES = 60_000; // keepalive request bodies are capped at 64 KB
  private static readonly MAX_MESSAGE_LENGTH = 1000;

  private static readonly tabId = PlaybackJournal.makeTabId();
  private static pending: JournalEntry[] = [];
  private static dropped = 0;
  private static installed = false;
  private static flushing = false;
  private static lastQuietVerdict: string | null = null;

  static install(): void {
    if (this.installed || typeof window === 'undefined') {
      return;
    }
    this.installed = true;

    ConsoleBufferService.subscribe(entry => this.add('log', entry.level, entry.message, entry.timestamp));

    this.event(`tab opened ${APP_VERSION} | ${navigator.userAgent}`);
    window.addEventListener('online', () => this.event('network online'));
    window.addEventListener('offline', () => this.event('network offline'));
    document.addEventListener('visibilitychange', () => {
      this.event(`visibility ${document.visibilityState}`);
      if (document.visibilityState === 'hidden') {
        void this.flush(true);
      }
    });
    window.addEventListener('pagehide', () => void this.flush(true));
    setInterval(() => void this.flush(false), this.FLUSH_INTERVAL_MS);
  }

  /**
   * One playback health evaluation. Polled verdicts (with detail) are kept
   * every time — they are the 5s timeline of real progress. Verdicts without
   * a poll (idle, held, reconnecting) repeat every cycle, so only changes are kept.
   */
  static health(verdict: string, detail?: string): void {
    if (!detail) {
      if (verdict === this.lastQuietVerdict) {
        return;
      }
      this.lastQuietVerdict = verdict;
    } else {
      this.lastQuietVerdict = null;
    }
    this.add('health', null, detail ? `${verdict} ${detail}` : verdict);
  }

  static event(message: string): void {
    this.add('event', null, message);
  }

  private static add(kind: JournalEntry['kind'], level: string | null, message: string, at?: string): void {
    if (!this.installed) {
      return;
    }
    this.pending.push({
      at: at ?? new Date().toISOString(),
      kind,
      level,
      message: message.slice(0, this.MAX_MESSAGE_LENGTH)
    });
    if (this.pending.length > this.MAX_PENDING) {
      this.pending.shift();
      this.dropped++;
    }
  }

  private static takeBatch(): JournalEntry[] {
    if (this.dropped > 0) {
      this.pending.unshift({
        at: this.pending[0]?.at ?? new Date().toISOString(),
        kind: 'event',
        level: null,
        message: `journal dropped ${this.dropped} older entries (queue full)`
      });
      this.dropped = 0;
    }
    const batch: JournalEntry[] = [];
    let bytes = 200;
    while (this.pending.length > 0 && batch.length < this.MAX_BATCH) {
      const size = JSON.stringify(this.pending[0]).length + 1;
      if (batch.length > 0 && bytes + size > this.MAX_BATCH_BYTES) {
        break;
      }
      bytes += size;
      batch.push(this.pending.shift()!);
    }
    return batch;
  }

  /** Returns false when the batch should be retried later. */
  private static async send(batch: JournalEntry[], keepalive: boolean): Promise<boolean> {
    try {
      const response = await fetch(`${environment.apiBaseUrl}/playback-log`, {
        method: 'POST',
        credentials: 'include',
        keepalive,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tabId: this.tabId, appVersion: APP_VERSION, entries: batch })
      });
      // 400 = malformed batch; retrying would fail forever
      return response.ok || response.status === 400;
    } catch {
      return false;
    }
  }

  /**
   * leaving=true (tab hidden / unloading): one keepalive batch, so the request
   * survives the page being frozen or torn down. Otherwise drain in batches.
   */
  private static async flush(leaving: boolean): Promise<void> {
    if (this.pending.length === 0) {
      return;
    }
    if (leaving) {
      const batch = this.takeBatch();
      if (!(await this.send(batch, true))) {
        this.pending.unshift(...batch);
      }
      return;
    }
    if (this.flushing) {
      return;
    }
    this.flushing = true;
    try {
      while (this.pending.length > 0) {
        const batch = this.takeBatch();
        if (!(await this.send(batch, false))) {
          this.pending.unshift(...batch); // offline or signed out: keep for the next flush
          break;
        }
      }
    } finally {
      this.flushing = false;
    }
  }

  private static makeTabId(): string {
    try {
      return crypto.randomUUID();
    } catch {
      return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    }
  }
}
